import {
  Dialog,
  DialogTitle,
  DialogContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  DialogActions,
  Button,
  Switch,
  Alert,
  CircularProgress,
  Typography,
} from '@mui/material';
import axios, { AxiosResponse } from 'axios';
import React, { useContext, useEffect, useState } from 'react';
import { timestampedMessage } from '../../common/multi-package-scanner/multi-package-scanner';
import { ErrorsFlattenner } from '../../common/errors-flattenner/errors-flattenner';
import TextFieldWithScan from '../../common/textfield-with-scan/textfield-with-scan';
import { IPaymentTask } from '../../../interfaces/delivery-company/tasks';
import { PaymentTaskState } from '../../../enums/tasks';
import { API_ENDPOINT } from '../../../configurations/global.config';
import { AuthContext } from '../../../contexts/user-context/user-context';
import SnackBar from '../../common/snack-bar/snack-bar';
import { MerchantInvoiceQRCodePrefix } from '../../../utils/merchant-invoices';
import { count } from 'console';
import CustomDialog from '../../common/custom-dialog/custom-dialog';

interface ScanPaymentDialogProps {
  onClose: () => void;
  onSubmit: () => void;
  setSnackBarResult: (t?: timestampedMessage) => void;
}

function ScanPaymentDialog({
  onClose,
  setSnackBarResult,
  onSubmit,
}: ScanPaymentDialogProps) {
  const [response, setResponse] = useState<AxiosResponse>();
  const [reason, setReason] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { user, config } = useContext(AuthContext);

  const handleScan = async (encoded_value: string) => {
    if (!encoded_value.startsWith(MerchantInvoiceQRCodePrefix)) {
      setSnackBarResult({
        timestamp: Date.now(),
        content: 'Not a Payment Task',
        level: 'error',
      });
      return;
    }

    if (encoded_value == null || encoded_value === '') {
      setSnackBarResult({
        timestamp: Date.now(),
        content: 'Nothing Scanned',
        level: 'error',
      });
      return;
    }

    const invoice_id_from_scan = encoded_value.slice(
      MerchantInvoiceQRCodePrefix.length
    );

    let payment_tasks: IPaymentTask[] = [];
    try {
      const response = await axios.get<{
        results: IPaymentTask[];
        count: number;
      }>(
        `${API_ENDPOINT}/api/v1/payment-tasks/?invoice=${invoice_id_from_scan}&status=${PaymentTaskState.SCHEDULED}`,
        config
      );
      payment_tasks = response.data.results;
      if (response.data.count === 0) {
        throw new Error('No tasks found');
      }
    } catch (e) {
      setSnackBarResult({
        timestamp: Date.now(),
        level: 'error',
        content: 'No payment task found',
      });
      return;
    }

    if (payment_tasks.length > 1) {
      setSnackBarResult({
        timestamp: Date.now(),
        level: 'error',
        content:
          'Found multiple scheduled payments! Please contact us. ' +
          payment_tasks.length,
      });
      return;
    }

    const matchingTask = payment_tasks[0];

    try {
      const url = `${API_ENDPOINT}/api/v1/payment-task/${matchingTask.id}/`;
      const requestData = {
        status: PaymentTaskState.OUT_FOR_PAYMENT,
      };
      const response = await axios.patch(url, requestData, config);
      setSnackBarResult({
        timestamp: Date.now(),
        content: 'Invoice updated',
        level: 'success',
      });
      onSubmit();
      return;
    } catch (e) {
      setSnackBarResult({
        timestamp: Date.now(),
        content: 'An Error Occured',
        level: 'error',
      });
    }
  };

  return (
    <CustomDialog open={true} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle> Scan QRCode</DialogTitle>
      <DialogContent>
        {isLoading ? (
          <>
            Scanning... <br /> <CircularProgress />
          </>
        ) : (
          <>
            {reason != '' && (
              <>
                <Alert
                  severity={
                    response && response.status >= 200 && response.status < 300
                      ? 'success'
                      : 'error'
                  }
                >
                  <>
                    {reason}

                    {response && response.status >= 300 && (
                      <ErrorsFlattenner rawErrors={response.data} />
                    )}
                  </>
                </Alert>
                <br />
              </>
            )}

            {
              <>
                <TextFieldWithScan
                  label="Invoice Id"
                  type="text"
                  variant="outlined"
                  disabled={false}
                  fullWidth
                  required
                  actOnEnterPressed
                  continuousScanning
                  value={''}
                  act={(value) => {
                    handleScan(value);
                  }}
                />
              </>
            }
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button disabled={isLoading} onClick={onClose}>
          Cancel
        </Button>
      </DialogActions>
    </CustomDialog>
  );
}

export default ScanPaymentDialog;
